import type { PremadeCharacter } from "@dndbeyond/character-rules-engine";
import type { Option } from "@dndbeyond/ttui/lexicon/components/Dropdown";

import { ALL_RULESETS, RulesetSelection } from "../constants";
import { getPrimarySource } from "./getPrimarySource";

export interface PremadeFilterCriteria {
  query: string;
  classes: Array<Option>;
  species: Array<Option>;
  levels: Array<Option>;
  sources: Array<Option>;
  ruleset: RulesetSelection;
}

/**
 * Returns the premades matching the search query and every selected filter.
 * An empty selection (or the "All" ruleset) doesn't filter.
 */
export const filterPremades = (
  premades: Array<PremadeCharacter>,
  { query, classes, species, levels, sources, ruleset }: PremadeFilterCriteria
): Array<PremadeCharacter> =>
  premades.filter((character) => {
    // Text search filter
    const matchesSearch = [
      character.characterData.name,
      character.characterData.characterLevel.toString(),
      `level ${character.characterData.characterLevel}`,
      `lvl ${character.characterData.characterLevel}`,
      character.characterData.primaryClassName,
      character.characterData.speciesName,
      ...character.sources.map((source) => source.sourceName),
    ]
      .join(" ")
      .toLowerCase()
      .includes(query.toLowerCase());

    // Class filter
    const matchesClass =
      classes.length === 0 ||
      classes.some(
        (opt) => opt.value === character.characterData.primaryClassName
      );

    // Species filter
    const matchesSpecies =
      species.length === 0 ||
      species.some((opt) => opt.value === character.characterData.speciesName);

    // Level filter
    const matchesLevel =
      levels.length === 0 ||
      levels.some(
        (opt) => opt.value === character.characterData.characterLevel.toString()
      );

    // Source filter (check if ANY source matches)
    const matchesSource =
      sources.length === 0 ||
      character.sources.some((source) =>
        sources.some((opt) => opt.value === source.sourceId.toString())
      );

    // Ruleset filter (uses the primary source, which is the ruleset shown on the card)
    const matchesRuleset =
      ruleset === ALL_RULESETS ||
      getPrimarySource(character).rulesetId === ruleset;

    return (
      matchesSearch &&
      matchesClass &&
      matchesSpecies &&
      matchesLevel &&
      matchesSource &&
      matchesRuleset
    );
  });
